import { BaseModel } from './base-model';

/** `note` is a page's shared or private layer, `as_built` one deviation, `sketch` a record's tagged mark (spec §15 rule 1) */
export type MarkupKind = 'note' | 'as_built' | 'sketch';

/** An as-built mark's review status (spec §15 rule 4) */
export type AsBuiltReviewStatus = 'open' | 'reviewed' | 'rejected' | 'incorporated';

export interface MarkupReview {
  actor: { uuid: string; name: string } | null;
  at: string;
  comment: string | null;
}

/** One status change of an as-built mark (GET /v1/markup/:id/review-events); `carried` events belong to the row it was carried from */
export interface MarkupReviewEvent {
  uuid: string;
  /** null for the carry event of a carried copy */
  from_status: AsBuiltReviewStatus | null;
  to_status: AsBuiltReviewStatus;
  comment: string | null;
  actor: { uuid: string; name: string } | null;
  at: string;
  carried?: boolean;
}

/** 7.2: the record an as-built mark was made from (`Markup.linked_record`, the As-built list's `linked_record`) */
export interface MarkupLinkedRecord {
  entity_type: string;
  uuid: string | null;
  label: string | null;
  deleted?: boolean;
  /**
   * Where the record lives, for opening it (the Linked here row's keys; those that don't apply are null). Null for issues
   * and RFIs, and when the record is unreadable or deleted.
   */
  parent?: MarkupLinkedRecordParent | null;
}

export interface MarkupLinkedRecordParent {
  site_uuid: string | null;
  /** 'YYYY-MM-DD' */
  report_date: string | null;
  daily_report_uuid: string | null;
  daily_diary_uuid: string | null;
  punchlist_uuid: string | null;
  warranty_uuid: string | null;
}

export class Markup extends BaseModel {
  // for CASL subject detection on code minification
  static get modelName() {
    return 'Markup';
  }

  /* tslint:disable */
  attachment_id: string;
  page_number: number;
  annotation_data: any;
  canvas_width: number;
  canvas_height: number;
  version: number;
  client_uuid: string;
  preview_url: string;
  visibility: 'shared' | 'private';
  link_uuid: string | null;
  carried_from_uuid: string | null;
  kind?: MarkupKind;
  /** As-built rows only */
  review_status?: AsBuiltReviewStatus | null;
  incorporated_revision_uuid?: string | null;
  /** As-built rows only: the latest review (a carried copy takes its original's) */
  last_review?: MarkupReview | null;
  /** W59: false when the caller is neither the author nor a project admin (the API then answers MARKUP_NOT_OWNER); absent = allowed */
  can_edit?: boolean;
  /**
   * 7.2, as-built rows only: the record the mark was made from (its link tag). uuid / label are null when the caller
   * may not open it; `deleted` once the record is gone (the mark stays reviewable). Null when untagged.
   */
  linked_record?: MarkupLinkedRecord | null;
  /* tslint:enable */
}
